import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ReceiptText, Bell, CheckCircle2, AlertTriangle, XCircle, Info, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState, StatusBadge } from "@/components/brand";
import { PromoList } from "@/pages/Content";
import { Skeleton } from "@/components/ui/skeleton";

const BetRow = ({ b }) => (
  <tr data-testid={`bet-row-${b.id}`} className={`border-t border-cr-line ${b.side === "lagai" || b.side === "yes" ? "border-l-4 border-l-[#72bbef]" : "border-l-4 border-l-[#faa9ba]"}`}>
    <td className="px-3 py-2 text-cr-muted">{dateTime(b.created_at)}</td>
    <td className="px-3 py-2">{b.match}</td>
    <td className="px-3 py-2 font-semibold">{b.name}</td>
    <td className="px-3 py-2 font-semibold uppercase">{b.side}</td>
    <td className="px-3 py-2">{b.line != null ? `${b.line} / ${b.rate}` : b.rate}</td>
    <td className="px-3 py-2">{b.amount.toLocaleString("en-IN")}</td>
    <td className="px-3 py-2 text-cr-gold">{b.win.toLocaleString("en-IN")}</td>
    <td className={`px-3 py-2 font-semibold capitalize ${b.status === "won" ? "text-emerald-400" : b.status === "lost" ? "text-cr-danger" : ""}`}>{b.status}
      {b.result_value != null && typeof b.result_value === "number" && <span className="ml-1 text-xs text-cr-muted">({b.result_value})</span>}
      {b.corrections?.length > 0 && <span className="ml-1 rounded bg-cr-gold/20 px-1.5 py-0.5 text-[10px] normal-case text-cr-gold">Corrected: {b.corrections[b.corrections.length - 1].reason}</span>}</td>
  </tr>
);

export function Bets() {
  const [bets, setBets] = useState(null);
  const [f, setF] = useState("all");
  useEffect(() => { api.get("/me/bets").then((r) => setBets(r.data)).catch(() => setBets([])); }, []);
  const rows = (bets || []).filter((b) => f === "all" || b.status === f);
  return (
    <div data-testid="bets-page">
      <PageTitle crumbs={["Account", "Bet History"]} title="Bet History" subtitle="All your match odds and session bets, every innings." />
      <div className="mb-4 flex flex-wrap gap-2">{["all", "pending", "won", "lost", "void"].map((s) => (
        <button key={s} data-testid={`bets-filter-${s}`} onClick={() => setF(s)} className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${f === s ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line"}`}>{s}</button>))}</div>
      {bets === null ? <Skeleton className="h-40 bg-cr-elevated" /> : rows.length === 0 ? (
        <EmptyState testid="bets-empty" icon={ReceiptText} title="No bets yet" text="You haven't placed any bets yet." action={<Link to="/sports" className="btn-outline text-sm">Browse sports</Link>} />
      ) : (
        <div className="card-cr overflow-x-auto"><table data-testid="bets-table" className="w-full min-w-[900px] text-sm">
          <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["Time", "Match", "Market", "Side", "Rate", "Amount", "Win", "Status"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((b) => <BetRow key={b.id} b={b} />)}</tbody></table></div>
      )}
    </div>
  );
}

export function Bonuses() {
  return (
    <div data-testid="bonuses-page">
      <PageTitle crumbs={["Account", "Bonuses & Promotions"]} title="Bonuses & Promotions" subtitle="Current offers with eligibility, expiry and full terms." />
      <div className="mb-6 card-cr p-5 text-sm">Bonus balance: <b data-testid="bonus-balance" className="text-cr-gold">{money(0)}</b></div>
      <PromoList testid="account-promos" />
    </div>
  );
}

const KIND = { success: [CheckCircle2, "text-cr-success"], warning: [AlertTriangle, "text-cr-gold"], error: [XCircle, "text-cr-danger"], info: [Info, "text-sky-400"], security: [ShieldAlert, "text-cr-danger"] };

export const NotificationItem = ({ n, onRead }) => {
  const [Icon, color] = KIND[n.kind] || KIND.info;
  return (
    <div data-testid={`notification-${n.id}`} className={`card-cr flex items-start gap-4 p-4 ${n.read ? "opacity-60" : ""}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${color}`} />
      <div className="flex-1"><p className="font-semibold">{n.title}</p><p className="text-sm text-cr-ink/75">{n.body}</p><p className="mt-1 text-xs text-cr-muted">{dateTime(n.created_at)}</p></div>
      {!n.read && <button data-testid={`notification-read-${n.id}`} onClick={() => onRead(n.id)} className="text-xs text-cr-gold">Mark read</button>}
    </div>
  );
};

export function Notifications() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/me/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const readOne = async (id) => { await api.post(`/me/notifications/${id}/read`); load(); };
  const readAll = async () => { await api.post("/me/notifications/read-all"); load(); };
  return (
    <div data-testid="notifications-page">
      <PageTitle crumbs={["Account", "Notifications"]} title="Notifications" subtitle="Transaction updates, verification notices and security alerts."
        right={<button data-testid="notifications-read-all" onClick={readAll} className="btn-outline text-sm">Mark all read</button>} />
      {items === null ? <Skeleton className="h-40 bg-cr-elevated" /> : items.length === 0 ? <EmptyState icon={Bell} title="You're all caught up" /> :
        <div className="space-y-3">{items.map((n) => <NotificationItem key={n.id} n={n} onRead={readOne} />)}</div>}
    </div>
  );
}

const TicketView = ({ t, onUpdate }) => {
  const [msg, setMsg] = useState("");
  const send = async () => {
    try { const { data } = await api.post(`/me/tickets/${t.id}/reply`, { message: msg }); setMsg(""); onUpdate(data); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="ticket-thread" className="card-cr p-5">
      <div className="flex items-center justify-between"><p className="font-semibold">{t.ticket_no} · {t.subject}</p><StatusBadge status={t.status} testid="ticket-thread-status" /></div>
      <div className="mt-4 space-y-3">{t.messages.map((m, i) => <div key={i} className={`rounded-lg p-3 text-sm ${m.from === "support" ? "border border-cr-gold/30 bg-cr-gold/5" : "bg-cr-elevated"}`}><p className="text-xs text-cr-muted">{m.from === "support" ? "CRBETS Support" : "You"} · {dateTime(m.at)}</p><p className="mt-1">{m.body}</p></div>)}</div>
      {t.status !== "closed" && <div className="mt-4 flex gap-2"><input data-testid="ticket-reply-input" className="input-cr" placeholder="Write a reply" value={msg} onChange={(e) => setMsg(e.target.value)} /><button data-testid="ticket-reply-btn" disabled={!msg.trim()} onClick={send} className="btn-gold">Send</button></div>}
    </div>
  );
};

export function Support() {
  const { config } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null);
  const [f, setF] = useState({ subject: "", category: "account", message: "" });
  const load = () => api.get("/me/tickets").then((r) => setTickets(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/me/tickets", f); toast.success(`Ticket ${data.ticket_no} created`); setF({ subject: "", category: "account", message: "" }); setActive(data); load(); }
    catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div data-testid="support-page">
      <PageTitle crumbs={["Account", "Support"]} title="Support" subtitle={`Raise a ticket or email ${config?.support?.email || "support"}. See also our FAQ.`} right={<Link to="/faq" data-testid="support-faq-link" className="btn-outline text-sm">FAQ</Link>} />
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={submit} data-testid="ticket-form" className="card-cr space-y-4 p-6">
          <p className="font-semibold">New ticket</p>
          <input data-testid="ticket-subject-input" required minLength={3} className="input-cr" placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          <select data-testid="ticket-category-select" className="input-cr" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
            {["account", "payments", "technical", "responsible_gaming", "complaint", "other"].map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <textarea data-testid="ticket-message-input" required minLength={5} rows={5} className="input-cr" placeholder="Describe your issue" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          <button data-testid="ticket-submit-btn" className="btn-gold w-full">Submit ticket</button>
        </form>
        <div className="space-y-4">
          <p className="font-semibold">Ticket history</p>
          {tickets === null ? <Skeleton className="h-24 bg-cr-elevated" /> : tickets.length === 0 ? <EmptyState testid="tickets-empty" title="No tickets yet" /> :
            tickets.map((t) => <button key={t.id} data-testid={`ticket-${t.ticket_no}`} onClick={() => setActive(t)} className="card-cr flex w-full items-center justify-between p-4 text-left hover:border-cr-gold/50"><div><p className="font-semibold">{t.ticket_no} · {t.subject}</p><p className="text-xs text-cr-muted">{dateTime(t.updated_at)}</p></div><StatusBadge status={t.status} /></button>)}
          {active && <TicketView t={active} onUpdate={(d) => { setActive(d); load(); }} />}
        </div>
      </div>
    </div>
  );
}
